import { useMemo } from 'react';

import cx from 'classnames';

import { CgClose } from 'react-icons/cg';
import { IoIosArrowDown } from 'react-icons/io';
import { IoEyeOutline } from 'react-icons/io5';
import { RiShareForwardFill } from 'react-icons/ri';
import { PiFunction } from 'react-icons/pi';
import { TbArrowsExchange } from 'react-icons/tb';

import { CalculatorLogo, DraggableIcon } from '@src/assets/icons';

import { OPopper, OTooltip } from '@src/shared';
import { useTheme } from '@src/contexts';

import { TCalculatorMode } from '@src/types';

import useCalculatorHeader from './useCalculatorHeader';

import styles from '../../calculator.module.scss';

import { useRoomContext } from '@src/contexts';

type TProps = {
  isReadOnly: boolean;
  mode: TCalculatorMode;
  onModeChange: (mode: TCalculatorMode) => void;
  className?: string;
};

const CalculatorHeader = ({ mode, isReadOnly, onModeChange, className }: TProps) => {
  const {
    isSharedByMe,
    handleShareCalculator,
    handleShareResultInPublicChat,
    handleCloseCalculator,
    handleStopShareCalculator,
    isSharedCalculatorOpened,
    sharedCalculatorOwner,
    sharedCalculatorSettings,
    canUserShareCalculator,
    isPublicChatEnabled,
    canShareResultInPublicChat,
  } = useCalculatorHeader({ mode, isReadOnly });

  const { theme } = useTheme();

  const shouldHideItem = isSharedCalculatorOpened && !isSharedByMe && !isReadOnly;
  const isSharedDisabled = shouldHideItem || !canUserShareCalculator;

  const { isMobileDevice } = useRoomContext();
  const iconSize = isMobileDevice && !isReadOnly ? 24 : 16;

  const modeIcon = useMemo(() => {
    switch (mode) {
      case 'basic':
        return <CalculatorLogo width={iconSize} height={iconSize} />;
      case 'scientific':
        return <PiFunction size={iconSize} />;
      case 'converter':
        return <TbArrowsExchange size={iconSize} />;
      default:
        return <CalculatorLogo width={iconSize} height={iconSize} />;
    }
  }, [mode, iconSize]);

  const modeName = useMemo(() => {
    switch (mode) {
      case 'basic':
        return 'Basic calculator';
      case 'scientific':
        return 'Scientific calculator';
      case 'converter':
        return 'Converter calculator';
      default:
        return '';
    }
  }, [mode]);

  return (
    <>
      <div className={cx(styles.header, styles.dragHeader, className)}>
        <div className={styles.calculatorHeader}>
          {!isReadOnly || isSharedByMe ? (
            <OPopper
              customStyles={{ root: { width: isMobileDevice ? '329px' : '270px', zIndex: 2000 } }}
              position="bottom-end"
              icon={
                <div className={styles.headerDropdown}>
                  {modeIcon}
                  <IoIosArrowDown color="#33BDFC" size={isMobileDevice ? 35 : 25} />
                </div>
              }
              offset={[0, 6]}
            >
              <div className={styles.popperItem} onClick={() => onModeChange('basic')}>
                <CalculatorLogo />
                <span>Basic</span>
              </div>

              <div className={styles.popperItem} onClick={() => onModeChange('scientific')}>
                <PiFunction size={16} />
                <span>Scientific</span>
              </div>

              <div className={styles.popperItem} onClick={() => onModeChange('converter')}>
                <TbArrowsExchange size={16} />
                <span>Convert</span>
              </div>

              <div className={styles.divider} />

              <OTooltip
                title={!isPublicChatEnabled ? 'Enable public chat first.' : ''}
                isSmall
                placement="right"
                backgroundColor="var(--calculator-tooltip-bg)"
              >
                <div
                  className={cx(styles.popperItem, { [styles.drop]: !canShareResultInPublicChat })}
                  onClick={canShareResultInPublicChat ? handleShareResultInPublicChat : undefined}
                >
                  <RiShareForwardFill size={16} className={cx({ [styles.hideItem]: !canShareResultInPublicChat })} />
                  <span className={cx({ [styles.hideItem]: !canShareResultInPublicChat })}>
                    Share Result In Public Chat
                  </span>
                </div>
              </OTooltip>

              <OTooltip
                title={
                  !canUserShareCalculator
                    ? 'Sharing disabled by host or co-host.'
                    : shouldHideItem
                      ? 'The calculator is shared by another user.'
                      : ''
                }
                isSmall
                placement="right"
                backgroundColor="var(--calculator-tooltip-bg)"
              >
                <div
                  className={cx(styles.popperItem, {
                    [styles.drop]: isSharedDisabled,
                  })}
                  onClick={
                    isSharedDisabled ? undefined : isSharedByMe ? handleStopShareCalculator : handleShareCalculator
                  }
                >
                  <IoEyeOutline size={16} className={cx(isSharedDisabled && [styles.drop, 'disabled'])} />
                  <span className={cx(isSharedDisabled && [styles.drop, 'disabled'])}>
                    {isSharedByMe ? 'Stop calculator sharing' : 'Share calculator with participants'}
                  </span>
                </div>
              </OTooltip>
            </OPopper>
          ) : (
            <div>{modeIcon}</div>
          )}
          {(!isReadOnly || isSharedByMe) && <div className={'text-white text-[18px] sm:hidden'}>{modeName}</div>}
        </div>

        {!isMobileDevice && (
          <DraggableIcon
            className={cx(styles.draggableIcon, { [styles.isLight]: theme === 'light' })}
            width="20px"
            height="20px"
          />
        )}

        {isReadOnly && !isSharedByMe && isMobileDevice && (
          <div className={cx(styles.calculatorInfo, styles.calculatorInfoMobile)}>
            {sharedCalculatorOwner?.peer_name ?? 'Unknown'}'s {sharedCalculatorSettings?.mode ?? 'basic'} calculator
          </div>
        )}

        {(!isReadOnly || isSharedByMe) && (
          <button className={styles.headerClose} onClick={handleCloseCalculator}>
            <CgClose size={12} className={styles.closeIcon} />
          </button>
        )}
      </div>
      {isReadOnly && !isSharedByMe && !isMobileDevice && (
        <div className={styles.calculatorInfo}>
          {sharedCalculatorOwner?.peer_name ?? 'Unknown'}'s {sharedCalculatorSettings?.mode ?? 'basic'} calculator
        </div>
      )}
    </>
  );
};

export default CalculatorHeader;
